import { PlusOutlined } from '@ant-design/icons'
import { Button, MoreInfoLink, PageHeader, Search } from '@lmc/ui-kit'
import actions from 'actions'
import { useAbilityAccess } from 'components/AccessControl'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { debounce } from 'lodash'
import { type ChangeEventHandler, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import ManagerFilters, {
  type AllManagerFilters
} from '../components/ManagerFilters'
import ManageTasksTable, {
  defaultSort,
  type SortObject
} from '../components/ManageTasksTable'
import type { TaskFiltersValue } from '../components/TaskFilters'
import useRecurringTasks from '../hooks/useRecurringTasks'
import { ADD_TODO_HASH_ROUTE } from '../utils/managerUtils'

const HELP_ARTICLE_URL =
  'https://support.logmycare.co.uk/en/articles/8295341-how-do-i-manage-my-scheduled-to-do-s'

export const initialFiltersValue: AllManagerFilters = {
  rangePicker: [null, null],
  residents: [],
  categories: [],
  type: [],
  categoryItems: [],
  search: '',
  frequency: []
}

interface NewManagerProps {
  testId?: string
}

const NewManager = ({ testId }: NewManagerProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [filterParams, setFilterParams] =
    useState<AllManagerFilters>(initialFiltersValue)
  const [sortParams, setSortParams] = useState<SortObject>(defaultSort)

  const { recurringTasks, refetch, pagination, setPage, isLoading } =
    useRecurringTasks({
      filters: filterParams,
      sort: sortParams
    })

  const location = useLocation()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (location.hash.includes(ADD_TODO_HASH_ROUTE)) {
      handleOpenCreateModal()
    }
  }, [location.hash])

  const onFilterChange = (value: TaskFiltersValue) => {
    setFilterParams((prevFilters) => ({
      ...prevFilters,
      ...value
    }))
  }

  const handleOpenCreateModal = () => {
    window.scrollTo(0, 0)
    dispatch(
      actions.openModal({
        modalId: 'createTask',
        data: {
          onComplete: () => {
            refetch()
            navigate(location.pathname)
          }
        }
      })
    )
    dispatch(actions.addForm({ formId: 'createTask' }))
  }

  const debouncedSearch = debounce((searchValue: string) => {
    setFilterParams((prev) => ({
      ...prev,
      search: searchValue
    }))
  }, 1024)

  const onSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    debouncedSearch(event.target.value.trim())
  }

  const hasRolePermission = useAbilityAccess([
    {
      i: 'create',
      this: 'TaskManagement'
    }
  ])

  return (
    <Wrapper data-testid={testId || 'todos-manager'}>
      <PageHeader
        title={
          <TitleContainer
            data-testid={testId ? `${testId}-title` : 'todos-manager-title'}
          >
            {t(toDosConstants.MANAGE_TODOS_TITLE)}
            <MoreInfoLink helpArticleUrl={HELP_ARTICLE_URL} />
          </TitleContainer>
        }
        data-testid={
          testId ? `${testId}-page-header` : 'todos-manager-page-header'
        }
      >
        <RighHeaderWrapper
          data-testid={
            testId ? `${testId}-header-actions` : 'todos-manager-header-actions'
          }
        >
          <Search
            onChange={onSearch}
            placeholder={t(toDosConstants.SEARCH_BY_TITLE)}
            data-cy="todos-manage-search"
            data-testid={testId ? `${testId}-search` : 'todos-manager-search'}
          />
          <ButtonHeaderStyled
            onClick={handleOpenCreateModal}
            variant="primary"
            icon={<PlusOutlined />}
            disabledByPermission={!hasRolePermission}
            data-cy="todos-manage-add-button"
            data-testid={
              testId
                ? `${testId}-add-todo-button`
                : 'todos-manager-add-todo-button'
            }
          >
            {t(toDosConstants.ADD_A_TODO)}
          </ButtonHeaderStyled>
        </RighHeaderWrapper>
      </PageHeader>
      <ManagerFilters
        onChange={onFilterChange}
        value={filterParams}
        data-testid={
          testId ? `${testId}-manager-filters` : 'todos-manager-manager-filters'
        }
      />
      <ManageTasksTable
        onSortChange={setSortParams}
        isLoading={isLoading}
        onChange={(page) => setPage(page)}
        pagination={pagination}
        recurringTasks={recurringTasks}
        refetch={refetch}
        data-testid={
          testId
            ? `${testId}-manage-tasks-table`
            : 'todos-manager-manage-tasks-table'
        }
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1392px;
  padding: 24px 24px 50px;
  margin: 0 auto;
  ${({ theme }) => theme.mediaQueries.tabletDown} {
    padding: 24px;
  }
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const RighHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const ButtonHeaderStyled = styled(Button)`
  white-space: nowrap;
`

export default NewManager
